import { getAtuacao } from '../delegacao/atuacao';
import { CODIGO_CERT_EXPIRADO } from './certificado';
import { parseValidationErrors } from './parseValidationErrors';

export type ErrorKind =
  | 'auth'                  // 401
  | 'forbidden'             // 403
  | 'payment_required'      // 402
  | 'not_found'             // 404
  | 'conflict'              // 409
  | 'validation'            // 422
  | 'certificate_required'  // 428 (type: certificate_required)
  | 'pdpj_required'         // 428 (type: pdpj_connection_required)
  | 'rate_limited'          // 429
  | 'service_unavailable'   // 503
  | 'not_implemented'       // 501
  | 'server'                // 500+
  | 'network'               // sem response
  | 'unknown';

export interface ClassifiedError {
  status: number | undefined;
  kind: ErrorKind;
  message: string;
  detail: string | null;
  isAuthError: boolean;
  /**
   * Discriminador (extension RFC 9457) do 403 do gate de acesso processual.
   * Usado pelo AccessOracle para diferenciar o tipo de negação (modal vs
   * EmptyState). Null quando ausente.
   */
  codigo: string | null;
  /**
   * Classificação (extension RFC 9457) do processo no 403. Null quando ausente.
   */
  tipoAcesso: string | null;
}

export interface AxiosLikeError {
  _authHandled?: boolean;
  friendlyMessage?: string;
  /**
   * Config da request original do axios — usado pelo Sentry para derivar um
   * fingerprint por operação (método + path normalizado). Opcional porque
   * nem todo erro classificado é um AxiosError.
   */
  config?: {
    url?: string;
    method?: string;
  };
  response?: {
    status?: number;
    data?: {
      detail?:
        | string
        | Array<{ loc?: string[]; msg?: string }>
        | { message?: string; pendencias?: Array<{ chave?: string; descricao?: string }> };
      // Extensions Problem Details do backend (handler de gate de acesso)
      codigo?: string;
      tipoAcesso?: string;
      // Discriminador do 428 (certificate_required vs pdpj_connection_required)
      type?: string;
    };
  };
}

const STATUS_KIND_MAP: Record<number, ErrorKind> = {
  401: 'auth',
  402: 'payment_required',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  422: 'validation',
  428: 'certificate_required',
  429: 'rate_limited',
  501: 'not_implemented',
  503: 'service_unavailable',
};

/** Contexto delegado: o que é da conta (certificado, PDPJ, plano) é do titular. */
const MENSAGENS_DO_TITULAR: Partial<Record<ErrorKind, (titular: string) => string>> = {
  certificate_required: (t) => `${t} precisa ter um certificado digital ativo para esta consulta.`,
  pdpj_required: (t) => `${t} precisa estar conectado à PDPJ para esta operação.`,
  payment_required: (t) => `A assinatura de ${t} não permite esta ação.`,
};

const KIND_MESSAGES: Record<ErrorKind, string> = {
  auth: 'Sua sessão expirou. Faça login novamente.',
  forbidden: 'Você não tem permissão para esta ação.',
  payment_required: 'Assinatura necessária para acessar este recurso.',
  not_found: 'Recurso não encontrado.',
  conflict: 'Conflito ao processar a solicitação.',
  validation: 'Dados inválidos. Verifique os campos.',
  certificate_required: 'Certificado digital necessário para esta consulta.',
  pdpj_required: 'Conecte-se à PDPJ para esta operação.',
  rate_limited: 'Muitas requisições. Aguarde um momento antes de tentar novamente.',
  not_implemented: 'Funcionalidade em integração com o autenticador. Disponível em breve.',
  service_unavailable: 'Serviço temporariamente indisponível. Tente novamente em alguns minutos.',
  server: 'Ocorreu um erro no servidor. Tente novamente mais tarde.',
  network: 'Sem conexão com o servidor. Verifique sua internet.',
  unknown: 'Erro inesperado. Tente novamente.',
};

function extractDetail(data: AxiosLikeError['response']): string | null {
  const detail = data?.data?.detail;
  if (!detail) return null;

  if (typeof detail === 'string') {
    // Backend RequestValidationError pode encapsular detail como JSON string
    try {
      const parsed = JSON.parse(detail);
      const inner = parsed?.detail;
      if (Array.isArray(inner)) {
        return parseValidationErrors(inner);
      }
    } catch {
      // Não é JSON — retornar como texto
    }
    return detail;
  }

  if (Array.isArray(detail)) {
    return parseValidationErrors(detail);
  }

  // Detail estruturado ({ message, pendencias }) — ex.: o 409 de /secretario/ativar.
  // Sem este ramo, o objeto é descartado (null) e o usuário vê só o fallback
  // genérico do kind, perdendo as pendências que o backend enviou.
  if (typeof detail === 'object') {
    const partes: string[] = [];
    if (typeof detail.message === 'string' && detail.message) {
      partes.push(detail.message);
    }
    if (Array.isArray(detail.pendencias)) {
      const descricoes = detail.pendencias
        .map((p) => (p && typeof p.descricao === 'string' ? p.descricao : null))
        .filter((d): d is string => !!d);
      if (descricoes.length > 0) {
        partes.push(descricoes.join('; '));
      }
    }
    return partes.length > 0 ? partes.join(' ') : null;
  }

  return null;
}

export function classifyError(error: unknown): ClassifiedError {
  const axiosError = error as AxiosLikeError;

  // Erro marcado pelo interceptor de 401
  if (axiosError?._authHandled === true) {
    return {
      status: axiosError.response?.status ?? 401,
      kind: 'auth',
      message: KIND_MESSAGES.auth,
      detail: extractDetail(axiosError.response) ?? null,
      isAuthError: true,
      codigo: null,
      tipoAcesso: null,
    };
  }

  const status = axiosError?.response?.status;

  // Sem response = erro de rede
  if (!status) {
    // Verificar se é um Error genérico (não-axios)
    if (error instanceof Error && !axiosError?.response) {
      return {
        status: undefined,
        kind: 'network',
        message: KIND_MESSAGES.network,
        detail: error.message,
        isAuthError: false,
        codigo: null,
        tipoAcesso: null,
      };
    }
    return {
      status: undefined,
      kind: 'unknown',
      message: error instanceof Error ? error.message : KIND_MESSAGES.unknown,
      detail: null,
      isAuthError: false,
      codigo: null,
      tipoAcesso: null,
    };
  }

  let kind: ErrorKind = STATUS_KIND_MAP[status] ?? (status >= 500 ? 'server' : 'unknown');
  // 428 cobre dois gates — distingue pelo `type` do problem+json para o front
  // exibir "Conecte-se à PDPJ" em vez de "cadastre certificado".
  if (status === 428 && axiosError.response?.data?.type === 'pdpj_connection_required') {
    kind = 'pdpj_required';
  }
  const detail = extractDetail(axiosError.response);
  const codigo = axiosError.response?.data?.codigo ?? null;
  const tipoAcesso = axiosError.response?.data?.tipoAcesso ?? null;

  // Mensagem amigável do interceptor (500+)
  if (axiosError.friendlyMessage) {
    return {
      status,
      kind,
      message: axiosError.friendlyMessage,
      detail,
      isAuthError: kind === 'auth',
      codigo,
      tipoAcesso,
    };
  }

  // Usar detail do backend se disponível (exceto para auth — mensagem fixa).
  // Na conta de outra pessoa, certificado e PDPJ são DO TITULAR: o detail do
  // backend fala com o dono ("cadastre seu certificado") e confundiria o delegado.
  const atuacao = getAtuacao();
  const doTitular = !atuacao
    ? undefined
    : kind === 'certificate_required' && codigo === CODIGO_CERT_EXPIRADO
      ? (t: string) => `O certificado digital de ${t} venceu. Os autos abrem quando ele enviar o renovado.`
      : MENSAGENS_DO_TITULAR[kind];
  const message = kind === 'auth'
    ? KIND_MESSAGES.auth
    : doTitular?.(atuacao!.titularNome) ?? (detail || KIND_MESSAGES[kind]);

  return {
    status,
    kind,
    message,
    detail,
    isAuthError: kind === 'auth',
    codigo,
    tipoAcesso,
  };
}
